// Location: assets/bal-delivery-rules.js
// What: Calculate eligible delivery dates from Japan-time order cutoffs.
// Why: Keep cart delivery choices consistent across customer time zones.
// RELEVANT FILES: tests/bal-delivery-rules.test.mjs, snippets/bal-cart-delivery.liquid

(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
  const PREFECTURES = Object.freeze([
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
    '岐阜県', '静岡県', '愛知県', '三重県',
    '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
    '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県',
    '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
  ]);

  const DEFAULT_CLOSURES = '2026-12-29/2027-01-06';
  // Origins: regular Kyoto 6048032 / Kobe 6500021; cool Kyoto only.
  // Official postcode calculator checked 2026-10-13; island exceptions remain manual.
  const TWO_DAY_PREFECTURES = ['北海道', '青森県', '岩手県', '秋田県'];
  const TWO_BUFFER_DAY_PREFECTURES = ['長崎県', '宮崎県', '鹿児島県'];
  const dateString = (timestamp) => new Date(timestamp).toISOString().slice(0, 10);
  const isDispatchDay = (timestamp) => [1, 4].includes(new Date(timestamp).getUTCDay());

  function parseDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) return NaN;
    const timestamp = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(timestamp) && dateString(timestamp) === value ? timestamp : NaN;
  }

  function parseClosedPeriods(value = DEFAULT_CLOSURES) {
    if (typeof value !== 'string') return null;
    if (!value.trim()) return [];
    const entries = value.trim().split(/,\s*|\r?\n/);
    const periods = entries.map(entry => {
      const parts = entry.trim().split('/');
      return parts.length === 2 ? parts.map(parseDate) : [NaN, NaN];
    });
    return periods.every(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && start <= end) ? periods : null;
  }

  function validateClosedPeriods(value = DEFAULT_CLOSURES) {
    return parseClosedPeriods(value) !== null;
  }

  function isConfigurationValid({ closedPeriods = DEFAULT_CLOSURES, transitDaysByPrefecture = {} } = {}) {
    return validateClosedPeriods(closedPeriods) && transitDaysByPrefecture !== null &&
      typeof transitDaysByPrefecture === 'object' && !Array.isArray(transitDaysByPrefecture) &&
      Object.entries(transitDaysByPrefecture).every(([prefecture, days]) =>
        PREFECTURES.includes(prefecture) && Number.isInteger(days) && days >= 1 && days <= 7);
  }

  function getDeliveryPlan(options = {}) {
    if (!options || typeof options !== 'object' || !isConfigurationValid(options)) return [];
    const { now = new Date(), prefecture, hasTerrine = false, hasCool = false, count = 14,
      closedPeriods = DEFAULT_CLOSURES, transitDaysByPrefecture = {} } = options;
    if (!PREFECTURES.includes(prefecture) || prefecture === '沖縄県') return [];
    if (!Number.isInteger(count) || count < 1 || count > 366) return [];
    let timestamp;
    try { timestamp = new Date(now).getTime(); } catch { return []; }
    if (!Number.isFinite(timestamp)) return [];
    const japanNow = new Date(timestamp + JST_OFFSET_MS);
    const year = japanNow.getUTCFullYear();
    if (!Number.isInteger(year) || year < 100 || year > 9998) return [];
    const today = Date.UTC(year, japanNow.getUTCMonth(), japanNow.getUTCDate());
    const weekday = japanNow.getUTCDay();
    const sunday = (7 - weekday) % 7;
    const wednesday = (10 - weekday) % 7;
    const productionDays = hasTerrine ? (sunday < wednesday ? 4 : 5) : 1;
    const firstDispatch = today + (Math.min(sunday, wednesday) + productionDays) * DAY_MS;
    const carrierDays = transitDaysByPrefecture[prefecture] ?? (TWO_DAY_PREFECTURES.includes(prefecture) ? 2 : 1);
    const bufferDays = TWO_BUFFER_DAY_PREFECTURES.includes(prefecture) ? 2 : 1;
    const periods = parseClosedPeriods(closedPeriods);
    const isOpenDispatch = (dispatch) => isDispatchDay(dispatch) &&
      !periods.some(([start, end]) => dispatch >= start && dispatch <= end);
    const results = [];
    // Merchant buffer preserves approved cold weekdays separately from actual carrier transit.
    for (let index = 0; index < count; index += 1) {
      const arrival = today + (7 + index) * DAY_MS;
      let dispatch = arrival - (carrierDays + bufferDays) * DAY_MS;
      const oldest = hasCool || hasTerrine ? dispatch : arrival - (carrierDays + 6) * DAY_MS;
      for (; dispatch >= Math.max(firstDispatch, oldest); dispatch -= DAY_MS) {
        if (!isOpenDispatch(dispatch)) continue;
        results.push({ date: dateString(arrival), dispatchDate: dateString(dispatch) });
        break;
      }
    }
    return results;
  }

  function getDeliveryDates(options) {
    return getDeliveryPlan(options).map(({ date }) => date);
  }

  function isPendingPrefecture() { return false; }

  globalThis.BalDeliveryRules = Object.freeze({ prefectures: PREFECTURES, isPendingPrefecture,
    getDeliveryDates, getDeliveryPlan, validateClosedPeriods, isConfigurationValid });
})();
